import { Grapheme } from '@/common/types/scopeAndSequence';
import { CreateAttempt } from '@/common/types/session';
import {
  playAsInAudio,
  playPhonemeAudio,
  playWordAudio,
  stopAudio,
} from '@/common/utils/audio';
import {
  getDistractorGraphemes,
  selectCorrectItem,
} from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getArticulatoryGestures,
  getGraphemeLevel,
  getWordById,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getMysticMatch,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { MysticMatchCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import { CardFeedback } from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { calculateProgress, shuffleItems } from '@/game/gameHelpers';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';

export class MysticMatchController extends MinigameController {
  get state() {
    return GameInstance.instance.mysticMatch;
  }

  override startRound() {
    const { generalGameState } = GameInstance.instance;

    if (generalGameState.isRoundEnded) {
      generalGameState.isRoundEnded = false;
    }

    const graphemesForRound = generalGameState.gameData.mysticMatch || [];

    GameInstance.instance.updateMysticMatch({
      graphemeSet: graphemesForRound,
    });
    this.deal();
  }

  deal() {
    const { state } = this;
    const graphemeSet = state.graphemeSet;
    const correctGraphemeCounts = state.correctGraphemeCounts;
    const previousGrapheme = state.correctCard?.grapheme;

    const student = getStudent();

    const correctGrapheme = selectCorrectItem(
      graphemeSet,
      correctGraphemeCounts,
      previousGrapheme,
    );

    if (!correctGrapheme) {
      console.error('Could not get correct card');
      return;
    }

    const correctCard = {
      id: uuidv4(),
      grapheme: correctGrapheme,
      isCorrect: true,
    };

    const level = GameInstance.instance.getShouldPlayCustomLevel()
      ? getGraphemeLevel(correctGrapheme.id)
      : student.level;
    const DISTRACTOR_COUNT = 2;
    const distractorGraphemes = getDistractorGraphemes(
      [correctGrapheme],
      level,
      DISTRACTOR_COUNT,
      true, // filter graphemes with same pronunciation
    );
    const distractorCards = distractorGraphemes.map((g) => ({
      id: uuidv4(),
      grapheme: g,
      isCorrect: false,
    }));

    GameInstance.instance.updateMysticMatch({
      correctCard: correctCard,
      dealtCards: shuffleItems([correctCard, ...distractorCards]),
    });
  }

  override async playAudioForRound() {
    const correctCard = getMysticMatch().correctCard;
    if (!correctCard) return;
    this.handleSegmentationAudioPlayed(correctCard.grapheme, 0, 1000);
    await playPhonemeAudio(correctCard.grapheme.pronunciation);
  }

  override async checkMinigameAnswer() {
    const { dealtCards, correctGraphemeCounts } = getMysticMatch();

    const student = getStudent();
    if (!student.id) {
      console.error('No student');
      return;
    }

    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity id found.');
      return;
    }

    const studentAttempt = this.minigame.getInput();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const correctCard = dealtCards.find(
      (card: MysticMatchCard) => card.isCorrect,
    );

    if (!correctCard) {
      console.error('No correct card found.');
      return;
    }

    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'mysticmatch',
      studentAnswer: studentAttempt,
      correctAnswer: correctCard.grapheme.spelling,
      correctAnswerId: correctCard.grapheme.id,
      incorrectAnswers: dealtCards
        .filter((card) => !card.isCorrect)
        .map((card) => card.grapheme.spelling),
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    const attemptWasCorrect = studentAttempt === correctCard.grapheme.spelling;

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(attemptWasCorrect);

    if (attemptWasCorrect) {
      // Handling for correct choice

      const newCount = correctGraphemeCounts[correctCard.grapheme.id] + 1 || 1;
      GameInstance.instance.updateMysticMatch({
        correctGraphemeCounts: {
          ...correctGraphemeCounts,
          [correctCard.grapheme.id]: newCount,
        },
      });

      const updatedProgress = calculateProgress(
        GameInstance.instance.generalGameState,
        GameInstance.instance.mysticMatch,
        100,
      );
      GameInstance.instance.updateGeneralGameState({
        progress: updatedProgress,
      });
      await this.showFeedback(this.getCardFeedback(), {
        correctAnswer: true,
        forceIndividualFeedback: true,
        disableRiffle: true,
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      await this.showFeedback(this.getCardFeedback(), {
        correctAnswer: false,
        forceIndividualFeedback: true,
        forceReadout: true,
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
      this.minigame.newRound();
    }
  }

  override getMaxBoardCards(): number {
    return 1;
  }

  override canPlayPhonemeOnCardDrop() {
    return false;
  }

  // Create cards for the minigame based on the state of this game.
  override createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    const dealtCards = GameInstance.instance.mysticMatch.dealtCards;
    return {
      dealtCards: dealtCards?.map((data) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, data, 'hand');
        return newCard;
      }),
      boardCards: [],
    };
  }

  override async playInstructions() {
    const correctCard = GameInstance.instance.mysticMatch.correctCard;

    if (!correctCard) {
      console.error('No correct card found.');
      return;
    }

    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Replay word button clicked', {
        word: correctCard.grapheme.spelling,
      });
    }

    this.handleSegmentationAudioPlayed(correctCard.grapheme, 0, 1000);
    await playPhonemeAudio(correctCard.grapheme.pronunciation);
  }

  override getCardFeedback(): CardFeedback[] {
    const submittedCard = this.minigame
      .getBoardDropSite()
      ?.layoutGroup?.getMinigameCards()[0] as StudentGameCard;
    const correctCard = GameInstance.instance.mysticMatch.correctCard;

    if (!submittedCard) {
      console.error('No submitted card found');
      return [];
    }
    return [
      {
        card: submittedCard,
        isCorrect:
          submittedCard.cardData?.grapheme?.spelling ===
          correctCard?.grapheme.spelling,
      },
    ];
  }

  override async handleIncorrectFeedbackComplete() {
    const handState = this.minigame.getHandDropSite().layoutGroup!;
    const correctAnswer = getMysticMatch().correctCard;
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }

    const correctCard = handState
      .getMinigameCards()
      .find(
        (card: StudentGameCard) =>
          card.cardData?.grapheme === correctAnswer?.grapheme,
      );

    const grapheme = correctCard?.cardData?.grapheme;
    if (!grapheme) {
      console.error('No grapheme found');
      return;
    }

    // Highlight the correct card
    if (correctCard) {
      if (!student.settings.articulatoryGesturesEnabled) {
        playPhonemeAudio(grapheme.pronunciation);
      }
      await this.doCardFeedback({ card: correctCard, isCorrect: true }, false);
    }

    await waitForSeconds(this.minigame.time, 0.4);
  }

  override handleSegmentationAudioPlayed(
    grapheme: Grapheme,
    _index: number,
    audioDelay: number,
  ) {
    console.log('Segmentation audio played for', grapheme.spelling);
    // Mystic Match shows the gesture when the segmentation audio plays
    // So long as the student has it enabled. Doesn't matter that art
    // gestures are generally disabled for this game since they can still be shown here.
    if (!getStudent().settings.articulatoryGesturesEnabled) return;

    const gestures = getArticulatoryGestures(grapheme.pronunciation);
    this.minigame.showHelperArticulatoryGestures(gestures, audioDelay);
  }

  override async playGraphemeExampleWord() {
    const correctCard = getMysticMatch().correctCard;
    if (!correctCard) {
      console.error('No correct card');
      return;
    }

    const grapheme = correctCard.grapheme;
    const exampleWord = getWordById(grapheme.example);
    if (!exampleWord) {
      console.error(
        `No example word found for grapheme: ${grapheme.spelling} /${grapheme.pronunciation}/`,
      );
      return;
    }

    stopAudio();
    trackMixpanelEvent('Grapheme example button clicked', {
      grapheme,
    });

    try {
      this.minigame.showDefinitionForWord(exampleWord);
    } catch (error) {
      console.error('Error showing icon', error);
    }

    await playPhonemeAudio(grapheme.pronunciation);
    await playAsInAudio();
    await playWordAudio(exampleWord.spelling);
  }
}
